import { useEffect, useState } from 'react'
import { api } from '../../lib/api'
import { Sparkles, AlertTriangle } from 'lucide-react'

interface BillingStatus {
  subscription_status: string
  trial_encounters_used: number
  trial_encounters_limit: number
  trial_remaining: number
  has_subscription: boolean
}

export function SubscriptionBanner() {
  const [status, setStatus] = useState<BillingStatus | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    api.getBillingStatus().then(setStatus).catch(() => {})
  }, [])

  if (!status) return null

  const { subscription_status, trial_remaining, trial_encounters_limit } = status

  // Active subscribers — no banner
  if (subscription_status === 'active') return null

  const handleUpgrade = async () => {
    setLoading(true)
    try {
      const { checkout_url } = await api.createCheckout()
      window.location.href = checkout_url
    } catch (e) {
      console.error('Checkout error:', e)
      setLoading(false)
    }
  }

  // Trial healthy (3+ left): quiet status chip, not a banner
  if (subscription_status === 'trial' && trial_remaining > 2) {
    return (
      <div className="flex items-center justify-end gap-2 mb-3">
        <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
          {trial_remaining} of {trial_encounters_limit} free visits left
        </span>
        <button
          onClick={handleUpgrade}
          disabled={loading}
          className="text-xs font-medium text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-400 disabled:opacity-50"
        >
          {loading ? '...' : 'Upgrade'}
        </button>
      </div>
    )
  }

  // Trial running low (1-2 left): visible banner
  if (subscription_status === 'trial' && trial_remaining > 0) {
    return (
      <div className="bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded-xl px-4 py-3 flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-indigo-700 dark:text-indigo-300">
          <Sparkles className="w-4 h-4 flex-shrink-0" />
          <span>
            Only <strong>{trial_remaining}</strong> free {trial_remaining === 1 ? 'visit' : 'visits'} left
          </span>
        </div>
        <button
          onClick={handleUpgrade}
          disabled={loading}
          className="text-xs font-medium bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-1.5 rounded-lg transition flex-shrink-0 disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Upgrade'}
        </button>
      </div>
    )
  }

  // Trial exhausted
  if (subscription_status === 'trial' && trial_remaining <= 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 mb-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-amber-900 mb-1">Free trial complete</p>
            <p className="text-sm text-amber-700 mb-3">
              You've used all {trial_encounters_limit} free encounters. Subscribe to continue recording and generating notes.
            </p>
            <button
              onClick={handleUpgrade}
              disabled={loading}
              className="bg-indigo-500 hover:bg-indigo-600 text-white font-medium px-4 py-2 rounded-lg transition text-sm disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Subscribe — $99/mo'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Past due
  if (subscription_status === 'past_due') {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-4 mb-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-rose-900 mb-1">Payment issue</p>
            <p className="text-sm text-rose-700 mb-3">
              Your last payment didn't go through. Please update your payment method to continue using Dr. Scribey.
            </p>
            <button
              onClick={async () => {
                try {
                  const { portal_url } = await api.createPortalSession()
                  window.location.href = portal_url
                } catch (e) {
                  console.error(e)
                }
              }}
              className="bg-rose-600 hover:bg-rose-700 text-white font-medium px-4 py-2 rounded-lg transition text-sm"
            >
              Update Payment Method
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Canceled
  if (subscription_status === 'canceled') {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-4 mb-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-gray-900 mb-1">Subscription canceled</p>
            <p className="text-sm text-gray-600 mb-3">
              Your subscription has been canceled. Resubscribe to continue recording and generating notes.
            </p>
            <button
              onClick={handleUpgrade}
              disabled={loading}
              className="bg-indigo-500 hover:bg-indigo-600 text-white font-medium px-4 py-2 rounded-lg transition text-sm disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Resubscribe — $99/mo'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return null
}
